import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, ShoppingCart, ExternalLink, Globe, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import { getStatusStyle } from '@/lib/orderStatusStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersListPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCountryMenu, setShowCountryMenu] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        const [ordersRes, companiesRes] = await Promise.all([
          axios.get(`${API}/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/companies`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setOrders(ordersRes.data.orders || []);
        setCompanies(companiesRes.data.companies || []);
      } catch (err) {
        const errorMessage = err.response?.data?.detail || 'Failed to load orders';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.company_name || 'Unknown Company';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatAmount = (amount) => {
    if (typeof amount !== 'number') return '-';
    return `$${amount.toFixed(2)}`;
  };
  
  // Helper to get order amount (use total_amount or fallback to final_amount)
  const getOrderAmount = (order) => {
    return order.total_amount ?? order.final_amount ?? null;
  };

  return (
    <AppLayout>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">My Orders</h1>
          <p className="text-gray-400 text-sm mt-1">Track and manage your service orders</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowCountryMenu(v => !v)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: '#059669' }}
              data-testid="new-order-btn"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">New Order</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {showCountryMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowCountryMenu(false)} />
                <div className="absolute right-0 mt-2 w-60 bg-[#0f1720] border border-gray-800 rounded-lg shadow-xl z-20 overflow-hidden" data-testid="new-order-country-menu">
                  <p className="px-3 pt-3 pb-1 text-xs text-gray-500 uppercase tracking-wide">Choose jurisdiction</p>
                  {[
                    { code: 'US', name: 'United States', iso: 'us', path: '/dashboard/orders/new' },
                    { code: 'UK', name: 'United Kingdom', iso: 'gb', path: '/dashboard/orders/international?country=UK' },
                    { code: 'CA', name: 'Canada', iso: 'ca', path: '/dashboard/orders/international?country=CA' },
                    { code: 'SG', name: 'Singapore', iso: 'sg', path: '/dashboard/orders/international?country=SG' },
                    { code: 'UAE', name: 'United Arab Emirates', iso: 'ae', path: '/dashboard/orders/international?country=UAE' },
                  ].map(j => (
                    <button
                      key={j.code}
                      onClick={() => { setShowCountryMenu(false); navigate(j.path); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-200 hover:bg-[#059669]/10 transition-colors"
                      data-testid={`new-order-country-${j.code}`}
                    >
                      <img src={`https://flagcdn.com/24x18/${j.iso}.png`} alt="" className="w-6 h-[18px] rounded-sm object-cover flex-shrink-0" />
                      {j.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Loading orders...</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && orders.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No orders yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Place your first order to get started with business formation and compliance services.
          </p>
          <Link
            to="/dashboard/orders/new"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: '#059669' }}
            data-testid="place-first-order-btn"
          >
            <Plus className="w-5 h-5" />
            Place Your First Order
          </Link>
        </div>
      )}

      {/* Orders Table */}
      {!loading && !error && orders.length > 0 && (
        <div className="bg-[#111827] border border-gray-800 rounded-lg overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-sm font-medium text-gray-500 px-6 py-4">Service</th>
                  <th className="text-left text-sm font-medium text-gray-500 px-6 py-4">Company</th>
                  <th className="text-left text-sm font-medium text-gray-500 px-6 py-4">Date</th>
                  <th className="text-left text-sm font-medium text-gray-500 px-6 py-4">Amount</th>
                  <th className="text-left text-sm font-medium text-gray-500 px-6 py-4">Status</th>
                  <th className="text-right text-sm font-medium text-gray-500 px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const statusStyle = getStatusStyle(order.status);
                  return (
                    <tr 
                      key={order.id} 
                      className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">{order.product_name || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-300">{getCompanyName(order.company_id)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-400">{formatDate(order.created_at)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white">{formatAmount(getOrderAmount(order))}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/dashboard/orders/${order.id}`}
                          className="text-[#059669] hover:underline text-sm flex items-center gap-1 justify-end"
                          data-testid={`view-order-${order.id}`}
                        >
                          View
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-gray-800">
            {orders.map((order) => {
              const statusStyle = getStatusStyle(order.status);
              return (
                <Link
                  key={order.id}
                  to={`/dashboard/orders/${order.id}`}
                  className="block p-4 hover:bg-gray-800/30 transition-colors"
                  data-testid={`order-card-${order.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-white font-medium">{order.product_name || 'N/A'}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                      {statusStyle.label}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-1">{getCompanyName(order.company_id)}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{formatDate(order.created_at)}</span>
                    <span className="text-white font-medium">{formatAmount(order.total_amount)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default OrdersListPage;
